import React, { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

interface RevealOnScrollProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Fades + slides its content in the first time it scrolls into view (500ms).
 * Used to stagger the order-detail sections as the dialog is scrolled so they
 * don't pop in abruptly.
 */
export const RevealOnScroll: React.FC<RevealOnScrollProps> = ({
  children,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Users who prefer reduced motion get the content immediately.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect(); // reveal once, then stay visible
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        // min-w-0 lets this grid/flex cell shrink below its content width so wide
        // children (tables) scroll inside their own overflow-auto wrapper instead
        // of stretching the whole column past the sheet width.
        "min-w-0 transition-all duration-500 ease-out motion-reduce:transition-none",
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
        className
      )}
    >
      {children}
    </div>
  );
};

interface RevealGroupProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps every element child in a <RevealOnScroll>, keeping the container's own
 * layout classes (e.g. the sections grid). Non-element children (the
 * `false`/`null` produced by conditional rendering) pass through untouched, so
 * no empty grid cells are created.
 */
export const RevealGroup: React.FC<RevealGroupProps> = ({
  children,
  className,
}) => {
  return (
    <div className={className}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? (
          <RevealOnScroll>{child}</RevealOnScroll>
        ) : (
          child
        )
      )}
    </div>
  );
};
